import type AliRtcEngine from 'aliyun-rtc-sdk';

let pendingTeardown: Promise<void> | null = null;

/**
 * 等待全局 AliRtcEngine 单例完成销毁。
 * 跨页面快速「返回 → 再进另一场 Chat」时，新会话必须先等上一场 teardown 结束。
 */
export const waitForRtcEngineTeardown = async (): Promise<void> => {
  if (pendingTeardown) {
    await pendingTeardown;
  }
};

/**
 * 串行销毁 RTC 引擎：先停本地/远端媒体，再 leaveChannel + destroy。
 * 同一时刻只跑一个 teardown，避免单例在 destroy 未完成时被下一场复用。
 */
export const teardownRtcEngine = (engine: AliRtcEngine): Promise<void> => {
  const work = async () => {
    try {
      engine.muteAllRemoteAudioPlaying(true);
      engine.subscribeAllRemoteAudioStreams(false);

      for (const uid of engine.getOnlineRemoteUsers()) {
        engine.subscribeRemoteAudioStream(uid, false);
        engine.stopRemoteAudioPlay(uid);
      }
    } catch {
      // 进入 teardown 时尽量先停掉旧远端音频，避免旧实例残留双声。
    }

    try {
      await engine.publishLocalAudioStream(false);
      engine.muteLocalMic(true);
      engine.stopAudioCapture();
    } catch {
      // 本地音频停止失败不阻断后续销毁。
    }

    try {
      await engine.publishLocalVideoStream(false);
      await engine.enableLocalVideo(false);
      await engine.stopPreview();
    } catch {
      // 本地视频停止失败不阻断后续销毁。
    }

    try {
      if (engine.isInCall()) {
        await engine.leaveChannel();
      }
    } catch {
      // 离开频道失败不阻断后续销毁。
    }

    try {
      await engine.destroy();
    } catch {
      // 引擎销毁失败无需额外处理。
    }
  };

  pendingTeardown = (pendingTeardown ?? Promise.resolve()).then(work, work);
  const promise = pendingTeardown;

  return promise.finally(() => {
    if (pendingTeardown === promise) {
      pendingTeardown = null;
    }
  });
};

/** 仅测试使用：重置模块级 teardown 队列。 */
export const resetRtcEngineTeardownForTests = (): void => {
  pendingTeardown = null;
};
